import { useMessagesStore } from "@/atoms/playground";
import { fetchStreamData } from "@/pages/Playground/components/fetcher";

import { ChatFormData, StreamAction, StreamActionItem } from "@/types";
import { conversationsApi } from "@/services/conversation-service";
import { createMessage } from "@/utils/chat/create-message";
import { parseStreamError } from "@/utils/chat/parsers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";

export const useStream = () => {
  const {
    messages,
    setMessages,
    pushReference,
    references,
    agentSteps: allAgentSteps,
    setAgentSteps,
    streamActions: allStreamActions,
    setStreamActions,
  } = useMessagesStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { id } = useParams();

  const agentSteps = id ? allAgentSteps[id] : undefined;
  const streamActions = id ? allStreamActions[id] : undefined;
  const [isPlanning, setIsPlanning] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [activeStreams, setActiveStreams] = useState<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const messageFlushFrameRef = useRef<number | null>(null);
  const pendingMessagesRef = useRef<any[] | null>(null);
  const pendingQueryUpdateRef = useRef<{
    queryKey: Array<string>;
    messages: any[];
    conversationId: string;
  } | null>(null);

  const flushPendingMessages = useCallback(() => {
    const pendingMessages = pendingMessagesRef.current;
    const pendingQuery = pendingQueryUpdateRef.current;

    messageFlushFrameRef.current = null;
    pendingMessagesRef.current = null;
    pendingQueryUpdateRef.current = null;

    if (
      pendingMessages &&
      pendingQuery &&
      window.location.pathname.includes(pendingQuery.conversationId)
    ) {
      startTransition(() => {
        setMessages([...pendingMessages]);
      });
    }

    if (pendingQuery) {
      queryClient.setQueryData(pendingQuery.queryKey, (old: any) => {
        if (!old) return old;
        return { ...old, messages: [...pendingQuery.messages] };
      });
    }
  }, [id, queryClient, setMessages]);

  const scheduleMessagesFlush = useCallback(
    (queryKey: Array<string>, messagesToFlush: any[]) => {
      pendingMessagesRef.current = messagesToFlush;
      pendingQueryUpdateRef.current = {
        queryKey,
        messages: messagesToFlush,
        conversationId: queryKey[1],
      };

      if (messageFlushFrameRef.current !== null) {
        return;
      }

      messageFlushFrameRef.current = window.requestAnimationFrame(() => {
        flushPendingMessages();
      });
    },
    [flushPendingMessages],
  );

  useEffect(() => {
    return () => {
      if (messageFlushFrameRef.current !== null) {
        window.cancelAnimationFrame(messageFlushFrameRef.current);
      }
    };
  }, []);

  const isStreaming = useCallback(
    (conversationId: string) => activeStreams.has(conversationId),
    [activeStreams],
  );

  const stop = useCallback((conversationId?: string) => {
    const targetId = conversationId || id;
    if (!targetId) return;

    const controller = abortControllersRef.current.get(targetId);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(targetId);
      setActiveStreams((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
      if (targetId === id) {
        setIsPlanning(false);
        setIsThinking(false);
      }
    }
  }, [id]);

  const hasOpenThinkTag = (content: string) => {
    const opens = (content.match(/<think>/g) || []).length;
    const closes = (content.match(/<\/think>/g) || []).length;
    return opens > closes;
  };

  const parseMarkdownLink = (text: string) => {
    const match = /^\[([\s\S]+?)\]\(([^)]*)\)$/.exec(text.trim());
    if (match) {
      return { label: match[1], url: match[2] };
    }
    return { label: text };
  };

  const getStreamActions = (conversationId: string) =>
    useMessagesStore.getState().streamActions[conversationId] || [];

  const completeRunningActions = (conversationId: string) => {
    const actions = getStreamActions(conversationId);
    if (!actions.some((action) => action.status === "running")) return;
    setStreamActions(
      conversationId,
      actions.map((action) =>
        action.status === "running"
          ? { ...action, status: "completed" as const }
          : action,
      ),
    );
  };

  const handleToolUseEvent = (conversationId: string, obj: any) => {
    const actions = getStreamActions(conversationId);

    if (obj.phase === "start") {
      let detail: string | undefined;
      if (typeof obj.input === "string") {
        detail = obj.input;
      } else if (obj.input && typeof obj.input === "object") {
        detail = Object.values(obj.input).find(
          (value): value is string => typeof value === "string",
        );
      }
      const newAction: StreamAction = {
        id: obj.id,
        name: obj.name || "",
        icon: obj.icon,
        status: "running",
        detail,
        items: [],
      };
      setStreamActions(conversationId, [
        ...actions.map((action) =>
          action.status === "running"
            ? { ...action, status: "completed" as const }
            : action,
        ),
        newAction,
      ]);
      return;
    }

    if (obj.phase === "processing") {
      const { label, url } = parseMarkdownLink(obj.name || "");
      const item: StreamActionItem = {
        id: `${obj.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        kind: "processing",
        label,
        url: url || (typeof obj.input === "string" ? obj.input : undefined),
        icon: obj.icon,
      };

      const existing = actions.find((action) => action.id === obj.id);
      if (existing) {
        setStreamActions(
          conversationId,
          actions.map((action) =>
            action.id === obj.id
              ? { ...action, items: [...action.items, item] }
              : action,
          ),
        );
      } else {
        // processing without a start event: create the action on the fly
        setStreamActions(conversationId, [
          ...actions,
          {
            id: obj.id,
            name: label,
            icon: obj.icon,
            status: "running",
            items: [],
          },
        ]);
      }
      return;
    }

    // any terminal phase (end / completed / error) closes the action
    setStreamActions(
      conversationId,
      actions.map((action) =>
        action.id === obj.id
          ? { ...action, status: "completed" as const }
          : action,
      ),
    );
  };

  const handleCompactionEvent = (conversationId: string, obj: any) => {
    const actions = getStreamActions(conversationId);
    const item: StreamActionItem = {
      id: `compaction-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: "compaction",
      label: obj.data?.tool || "",
      detail: obj.data?.tokens,
    };

    let targetIndex = -1;
    let lastRunningIndex = -1;
    for (let i = actions.length - 1; i >= 0; i--) {
      if (actions[i].status !== "running") continue;
      if (lastRunningIndex === -1) lastRunningIndex = i;
      if (!obj.data?.tool || actions[i].name === obj.data.tool) {
        targetIndex = i;
        break;
      }
    }
    const fallbackIndex = targetIndex !== -1 ? targetIndex : lastRunningIndex;

    if (fallbackIndex !== -1) {
      setStreamActions(
        conversationId,
        actions.map((action, index) =>
          index === fallbackIndex
            ? { ...action, items: [...action.items, item] }
            : action,
        ),
      );
    } else {
      setStreamActions(conversationId, [
        ...actions,
        {
          id: item.id,
          name: obj.data?.tool || "compaction",
          status: "completed",
          items: [item],
        },
      ]);
    }
  };

  const { mutate: streamMessage, isPending } = useMutation({
    mutationFn: async ({
      data,
      conversation_id,
    }: {
      data: ChatFormData;
      conversation_id: string;
    }) => {
      if (activeStreams.has(conversation_id)) {
        return;
      }

      setAgentSteps(conversation_id, undefined);
      setStreamActions(conversation_id, undefined);
      setIsPlanning(false);
      setIsThinking(false);

      const controller = new AbortController();
      abortControllersRef.current.set(conversation_id, controller);
      setActiveStreams((prev) => new Set(prev).add(conversation_id));

      const streamingPayload = {
        message: data.message,
        base_llm_config: {
          id: data.llm_config_id,
          temperature: data.temperature,
          max_tokens: data.max_tokens,
        },
        ...(data.mode !== "agentic"
          ? {
              planner_llm_config: {
                id: data.planner_llm_config,
                temperature: data.temperature,
                max_tokens: data.max_tokens,
              },
            }
          : {}),
        metadata: {
          tools: data.metadata?.tools || [],
          language: data.metadata?.language || "ar",
        },
        mode: data.mode,
      };
      const updated = [...messages];
      const newMessage = createMessage({
        content: data.message,
        conversation_id,
        metadata: data.metadata,
      });
      updated.push(newMessage);
      setMessages(updated);

      const queryKey = [
        "conversation",
        conversation_id,
        location.pathname.split("/")[1],
      ];
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return { ...old, messages: updated };
      });

      let buffer = "";
      let currentContent = "";
      let inThinkingBlock = false;
      const closeThinkingBlock = () => {
        if (inThinkingBlock) {
          currentContent += "</think>";
          inThinkingBlock = false;
        }
      };
      const finalizeThinking = () => {
        const wasOpen = inThinkingBlock;
        closeThinkingBlock();
        setIsThinking(false);
        if (wasOpen) {
          const lastIndex = updated.length - 1;
          if (lastIndex >= 0 && updated[lastIndex].role === "assistant") {
            updated[lastIndex] = {
              ...updated[lastIndex],
              content: currentContent,
            };
            scheduleMessagesFlush(queryKey, [...updated]);
          }
        }
      };

      try {
        const stream = await fetchStreamData(
          conversation_id,
          streamingPayload,
          controller.signal,
        );

        for await (const chunk of stream) {
          buffer += chunk;

          while (true) {
            const startIndex = buffer.indexOf("{");
            if (startIndex === -1) {
              if (buffer.includes("[DONE]")) {
                finalizeThinking();
                return data;
              }
              break;
            }

            let balance = 0;
            let endIndex = -1;
            let inString = false;
            let escape = false;

            for (let i = startIndex; i < buffer.length; i++) {
              const char = buffer[i];
              if (escape) {
                escape = false;
                continue;
              }
              if (char === "\\") {
                escape = true;
                continue;
              }
              if (char === '"') {
                inString = !inString;
                continue;
              }
              if (!inString) {
                if (char === "{") balance++;
                else if (char === "}") {
                  balance--;
                  if (balance === 0) {
                    endIndex = i;
                    break;
                  }
                }
              }
            }

            if (endIndex !== -1) {
              const jsonStr = buffer.substring(startIndex, endIndex + 1);
              buffer = buffer.substring(endIndex + 1);

              try {
                const obj = JSON.parse(jsonStr);

                if (obj.event === "title_generated") {
                  queryClient.invalidateQueries({
                    queryKey: ["conversations"],
                  });
                  queryClient.setQueryData(queryKey, (old: any) => {
                    if (!old) return old;
                    return { ...old, name: obj.data };
                  });
                }
                if (obj.event === "error") {
                  closeThinkingBlock();
                  currentContent += parseStreamError(obj.message).message;
                  setIsPlanning(false);
                  setIsThinking(false);
                } else if (obj.event === "heartbeat") {
                  if (data.mode === "simple") {
                    setIsThinking(true);
                  }
                } else if (obj.event === "thinking") {
                  if (!inThinkingBlock) {
                    currentContent += "<think>";
                    inThinkingBlock = true;
                  }
                  currentContent += obj.data;
                  setIsThinking(true);
                } else if (obj.event === "content") {
                  closeThinkingBlock();
                  currentContent += obj.data;
                  setIsThinking(hasOpenThinkTag(currentContent));
                  completeRunningActions(conversation_id);
                } else if (obj.event === "tool_use") {
                  handleToolUseEvent(conversation_id, obj);
                } else if (obj.event === "compaction") {
                  handleCompactionEvent(conversation_id, obj);
                } else if (obj.event === "finalize") {
                  closeThinkingBlock();
                  setIsThinking(false);
                  completeRunningActions(conversation_id);
                  if (obj.status === "FAILED") {
                    currentContent += obj.message_preview;
                  }
                } else if (obj.event === "planner_start") {
                  setIsPlanning(true);
                  setIsThinking(false);
                } else if (obj.event === "plan_generated") {
                  setIsPlanning(false);
                  setIsThinking(false);
                  const current =
                    useMessagesStore.getState().agentSteps[conversation_id];
                  setAgentSteps(conversation_id, {
                    ...current,
                    steps: obj.data.steps,
                  } as any);
                  console.log(obj.data.steps);
                } else if (obj.event === "reference") {
                  const newReferences = {
                    ...references,
                    [obj.data.type]: {
                      ...references?.[obj.data.type],
                      [obj.data.id]: obj.data,
                    },
                  };
                  pushReference(newReferences);
                } else if (obj.event === "step_update" && obj.data.id) {
                  const prev =
                    useMessagesStore.getState().agentSteps[conversation_id];
                  if (prev) {
                    const steps = prev.steps.map((step) => {
                      if (step.id === parseInt(obj.data.id)) {
                        return { ...step, status: obj.data.status };
                      }
                      return step;
                    });
                    setAgentSteps(conversation_id, {
                      ...prev,
                      steps,
                      currentStep: parseInt(obj.data.id),
                    });
                  }
                } else if (obj.event === "query_end") {
                  completeRunningActions(conversation_id);
                  const prev =
                    useMessagesStore.getState().agentSteps[conversation_id];
                  if (prev) {
                    const steps = prev.steps.map((step) => {
                      if (step.status === "running") {
                        return { ...step, status: "completed" as const };
                      }
                      return step;
                    });
                    setAgentSteps(conversation_id, { ...prev, steps });
                  }
                }

                const lastIndex = updated.length - 1;
                if (lastIndex >= 0 && updated[lastIndex].role === "assistant") {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    content: currentContent,
                  };
                } else {
                  const newMessage = createMessage({
                    role: "assistant",
                    content: currentContent,
                    conversation_id,
                  });
                  updated.push(newMessage);
                }
                scheduleMessagesFlush(queryKey, [...updated]);
              } catch (error) {
                console.log("Stream parsing error:", error);
              }
            } else {
              break;
            }
          }
        }
        finalizeThinking();
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Stream aborted");
          finalizeThinking();
          setAgentSteps(conversation_id, undefined);
          setStreamActions(conversation_id, undefined);
          setIsPlanning(false);
          setIsThinking(false);
        } else {
          // console.error("Stream error:", error);

          toast.error(
            typeof error.message === "string"
              ? error.message
              : "Failed to stream response. Please try again.",
          );
        }
      } finally {
        if (messageFlushFrameRef.current !== null) {
          window.cancelAnimationFrame(messageFlushFrameRef.current);
          flushPendingMessages();
        }
        abortControllersRef.current.delete(conversation_id);
        // Refresh persisted IDs in the background without delaying the next chat turn.
        const lastTemporaryId = updated[updated.length - 1]?.id;
        void conversationsApi.getConversation(conversation_id).then((saved) => {
          const cached = queryClient.getQueryData<any>(queryKey);
          if (
            abortControllersRef.current.has(conversation_id) ||
            cached?.messages?.[cached.messages.length - 1]?.id !== lastTemporaryId ||
            !saved.messages || saved.messages.length < updated.length
          ) return;
          queryClient.setQueryData(queryKey, saved);
          const current = useMessagesStore.getState().messages;
          if (
            current[current.length - 1]?.id === lastTemporaryId &&
            window.location.pathname.includes(conversation_id)
          ) setMessages(saved.messages);
        }).catch(() => {
          // Keep the completed response visible if persistence refresh fails.
        });

        setActiveStreams((prev) => {
          const next = new Set(prev);
          next.delete(conversation_id);
          return next;
        });
      }
      return data;
    },
  });
  const reset = useCallback(() => {
    setIsPlanning(false);
    setIsThinking(false);
  }, []);

  return {
    streamMessage,
    agentSteps,
    streamActions,
    isPending,
    isPlanning,
    isThinking,
    reset,
    stop,
    isStreaming,
  };
};
